import { BagPosterProps, PosterVariationWithQuantityProps } from "~/types/cart";
import { devtools, persist } from "zustand/middleware";

import { create } from "zustand";

interface CartItem extends BagPosterProps {
  variation: PosterVariationWithQuantityProps;
}

interface State {
  cart: BagPosterProps[];
  totalItems: number;
  totalPrice: number;
  showCart: boolean;
}

interface Actions {
  addToCart: (Item: BagPosterProps) => void;
  removeFromCart: (item: BagPosterProps) => void;
  toggleShowCart: () => void;
  resetCart: () => void;
}

export const createCartState = (market: string) =>
  create<State & Actions>()(
    devtools(
      persist(
        (set, get) => ({
          cart: [],
          totalItems: 0,
          totalPrice: 0,
          showCart: false,
          toggleShowCart: () => set((state) => ({ showCart: !state.showCart })),

          addToCart: (product: BagPosterProps) => {
            const cart = get().cart;

            const calculateVariationPrice = (
              variation: PosterVariationWithQuantityProps
            ) => Number(variation.price);

            const productKey = JSON.stringify({
              slug: product.slug,
              variation: product.variation,
            });

            const existingCartItemIndex = cart.findIndex((item) => {
              const itemKey = JSON.stringify({
                slug: item.slug,
                variation: item.variation,
              });
              return itemKey === productKey;
            });

            if (existingCartItemIndex !== -1) {
              const updatedCart: CartItem[] = cart.map((item, index) =>
                index === existingCartItemIndex
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              );

              const newTotalPrice = updatedCart.reduce(
                (sum, item) =>
                  sum + calculateVariationPrice(item.variation) * item.quantity,
                0
              );

              set((state) => ({
                cart: updatedCart,
                totalItems: state.totalItems + 1,
                totalPrice: newTotalPrice,
              }));
            } else {
              const totalPrice = calculateVariationPrice(product.variation);

              const updatedCart: CartItem[] = [
                ...cart,
                { ...product, quantity: 1 },
              ];

              const newTotalPrice = updatedCart.reduce(
                (sum, item) =>
                  sum + calculateVariationPrice(item.variation) * item.quantity,
                0
              );

              set((state) => ({
                cart: updatedCart,
                totalItems: state.totalItems + 1,
                totalPrice: state.totalPrice + totalPrice,
              }));
            }
          },

          removeFromCart: (product: BagPosterProps) => {
            const cart = get().cart;

            const calculateVariationPrice = (
              variation: PosterVariationWithQuantityProps
            ) => Number(variation.price);

            const updatedCart: CartItem[] = cart.filter(
              (item) =>
                item.slug !== product.slug ||
                JSON.stringify(item.variation) !==
                  JSON.stringify(product.variation)
            );

            const newTotalItems = updatedCart.reduce(
              (sum, item) => sum + item.quantity,
              0
            );

            const newTotalPrice = updatedCart.reduce(
              (sum, item) =>
                sum + calculateVariationPrice(item.variation) * item.quantity,
              0
            );

            set((state) => ({
              cart: updatedCart,
              totalItems: newTotalItems,
              totalPrice: newTotalPrice,
            }));
          },

          resetCart: () => {
            set({
              cart: [],
              totalItems: 0,
              totalPrice: 0,
            });
          },
        }),
        { name: `nps-${market}-bag-state` }
      )
    )
  );
